import React, { useState, useEffect } from 'react'
import { useParams, useLocation, Link, useNavigate, useSearchParams } from 'react-router-dom'
import { format } from 'date-fns'
import { fetchReportPhotos, getPhotoUrl, fetchReportById } from '../lib/supabase'
import { ArrowLeft, Calendar, Image, AlertTriangle, CheckCircle, ExternalLink } from 'lucide-react'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'

const ReportDetail = ({ merchantId }) => {
  const { orderId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const reportFromState = location.state?.report
  const [report, setReport] = useState(reportFromState || null)
  const [photos, setPhotos] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedPhoto, setSelectedPhoto] = useState(null)
  const [currentMerchantId, setCurrentMerchantId] = useState(merchantId)

  // Extract merchantId from URL hash if not provided via props
  useEffect(() => {
    const extractMerchantIdFromHash = () => {
      // First check if we already have a valid merchantId
      if (merchantId) {
        setCurrentMerchantId(merchantId);
        return;
      }
      
      try {
        // Try to get merchantId from hash params
        const hashString = window.location.hash;
        if (hashString && hashString.includes('?')) {
          const queryString = hashString.substring(hashString.indexOf('?'));
          const hashParams = new URLSearchParams(queryString);
          
          const midFromHash = hashParams.get('mid') || hashParams.get('merchant_id');
          if (midFromHash) {
            console.log(`Found merchantId in hash: ${midFromHash}`);
            setCurrentMerchantId(midFromHash);
            return;
          }
        }
        
        // Try to get merchantId from URL params
        const midFromUrl = searchParams.get('mid') || searchParams.get('merchant_id');
        if (midFromUrl) {
          console.log(`Found merchantId in URL params: ${midFromUrl}`);
          setCurrentMerchantId(midFromUrl);
          return;
        }
        
        // Try to get merchantId from report if available
        if (reportFromState?.merchant_id) {
          console.log(`Using merchantId from report state: ${reportFromState.merchant_id}`);
          setCurrentMerchantId(reportFromState.merchant_id);
          return;
        }
        
        console.warn("No merchantId found in props, URL, hash, or report state");
      } catch (error) {
        console.error("Error extracting merchantId:", error);
      }
    };
    
    extractMerchantIdFromHash();
  }, [merchantId, searchParams, reportFromState]);

  // If we don't have the report from state, fetch it
  useEffect(() => {
    const loadReport = async () => {
      if (!reportFromState && orderId && currentMerchantId) {
        try {
          console.log(`Fetching report data for order ID: ${orderId}, merchant ID: ${currentMerchantId}`);
          const reportData = await fetchReportById(orderId, currentMerchantId);
          if (reportData) {
            console.log(`Report data fetched successfully for ${orderId}`);
            setReport(reportData);
          } else {
            console.error(`No report found for order ID: ${orderId}`);
            setError(`No report found for order ID: ${orderId}`);
          }
        } catch (err) {
          console.error(`Error fetching report for order ID ${orderId}:`, err);
          setError(`Failed to load report details. Error: ${err.message}`);
        }
      }
    };
    
    if (currentMerchantId) {
      loadReport();
    }
  }, [orderId, currentMerchantId, reportFromState]);

  // Load photos once we have the report
  useEffect(() => {
    const loadPhotos = async () => {
      setLoading(true)
      setError(null)
      try {
        if (!orderId || !currentMerchantId) {
          console.error(`Cannot load photos, missing data: orderId=${orderId}, merchantId=${currentMerchantId}`);
          setError('Missing order ID or merchant ID')
          setLoading(false)
          return
        }

        console.log(`Loading photos for order ${orderId} and merchant ${currentMerchantId}`);
        const photoData = await fetchReportPhotos(orderId, currentMerchantId)
        console.log(`Fetched ${photoData ? photoData.length : 0} photos`);

        // Get public URLs for each photo with error handling
        const photosWithUrls = []
        for (const photo of photoData) {
          try {
            const url = await getPhotoUrl(photo.file_path)
            if (url) {
              photosWithUrls.push({ ...photo, url })
              console.log(`Got URL for photo: ${photo.id}`);
            }
          } catch (photoError) {
            console.error(`Failed to get URL for photo ${photo.id}:`, photoError)
            // Skip this photo but continue processing others
          }
        }

        setPhotos(photosWithUrls)
        if (photosWithUrls.length > 0) {
          setSelectedPhoto(photosWithUrls[0])
        }
      } catch (err) {
        console.error('Failed to fetch photos:', err)
        setError('Failed to load photos. Please try again.')
        setPhotos([])
      } finally {
        setLoading(false)
      }
    }

    if (report || (orderId && currentMerchantId)) {
      loadPhotos()
    }
  }, [orderId, currentMerchantId, report])

  // Safe format date function with better error handling
  const safeFormatDate = (dateString) => {
    try {
      if (!dateString) return 'Unknown date';
      
      // Create a date object
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        console.warn(`Invalid date format: ${dateString}`);
        return 'Invalid date';
      }
      
      return format(date, 'yyyy-MM-dd HH:mm:ss');
    } catch (error) {
      console.error('Date formatting error:', error, 'for date:', dateString);
      return 'Date error';
    }
  };

  // Format description items with error handling
  const formatDescription = (description) => {
    if (!description) return []
    try {
      return description.split(';').map(item => {
        const parts = item.split(': ')
        if (parts.length >= 2) {
          const category = parts[0]
          const details = parts.slice(1).join(': ') // Rejoin in case there were colons in the details
          return { category, details }
        }
        return { category: 'Note', details: item }
      })
    } catch (error) {
      console.error('Error formatting description:', error)
      return [{ category: 'Description', details: description }]
    }
  }

  const descriptionItems = report?.description ? formatDescription(report.description) : []

  // Handle image error
  const handleImageError = (e) => {
    e.target.onerror = null
    e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='36' height='36' viewBox='0 0 24 24' fill='none' stroke='%23ccc' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='3' width='18' height='18' rx='2' ry='2'/%3E%3Ccircle cx='8.5' cy='8.5' r='1.5'/%3E%3Cpolyline points='21 15 16 10 5 21'/%3E%3C/svg%3E"
  }

  // Return to list when there's an error or no report
  const handleReturnToList = () => {
    // Get current parameters
    let params = {};
    
    // First try to get from currentMerchantId
    if (currentMerchantId) {
      params.mid = currentMerchantId;
    }
    
    // Try to get from hash params
    const hashString = window.location.hash;
    if (hashString && hashString.includes('?')) {
      const queryString = hashString.substring(hashString.indexOf('?'));
      const hashParams = new URLSearchParams(queryString);
      
      // Merchant ID
      if (!params.mid && hashParams.has('mid')) {
        params.mid = hashParams.get('mid');
      }
      
      // Location parameter
      if (hashParams.has('loc')) {
        params.loc = hashParams.get('loc');
      }
      
      // Store ID parameter
      if (hashParams.has('sid')) {
        params.sid = hashParams.get('sid');
      }
    }
    
    // If we still don't have a merchant ID, try URL params
    if (!params.mid) {
      const midFromUrl = searchParams.get('mid') || searchParams.get('merchant_id');
      if (midFromUrl) {
        params.mid = midFromUrl;
      }
    }
    
    // If we still don't have a merchant ID, try report data
    if (!params.mid && report?.merchant_id) {
      params.mid = report.merchant_id;
    }
    
    // Ensure we have a merchant ID to prevent "No merchant selected" error
    if (!params.mid) {
      console.error("No merchant ID found for navigation");
      
      // Last resort - try to extract from URL using regex
      const url = window.location.href;
      const midMatch = url.match(/[?&#](mid|merchant_id|merchantId|merchant-id)=([^&#]*)/i);
      if (midMatch && midMatch[2]) {
        params.mid = decodeURIComponent(midMatch[2]);
      }
    }
    
    // Create the hash query string
    const hashQuery = new URLSearchParams(params).toString();
    
    console.log("Navigating back to list with params:", params);
    
    // Navigate back to the list with parameters in hash
    navigate({
      pathname: '/',
      search: '', // Clear URL search params
      hash: hashQuery ? `?${hashQuery}` : ''
    });
  }
  
  // Back button that preserves parameters
  const handleBackClick = (e) => {
    e.preventDefault();
    handleReturnToList();
  }

  // If we don't have a report, show a message
  if (!report && !loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button onClick={handleBackClick} className="p-2 rounded-full hover:bg-gray-100">
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            Report Not Found
          </h1>
        </div>
        <div className="card p-6 text-center">
          <SafeIcon icon={FiIcons.FiAlertCircle} className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <p className="text-gray-700 mb-4">
            {error || "The report details could not be loaded. This might happen if you accessed this page directly or refreshed the browser."}
          </p>
          <button onClick={handleReturnToList} className="btn-primary inline-block">
            Return to Reports List
          </button>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button onClick={handleBackClick} className="p-2 rounded-full hover:bg-gray-100">
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            Loading Report...
          </h1>
        </div>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  // Use created_at_vienna if available, otherwise fall back to created_at
  const reportDate = report.created_at_vienna || report.created_at;

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button onClick={handleBackClick} className="p-2 rounded-full hover:bg-gray-100">
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          Return Report: {orderId}
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">Report Details</h2>
              <a
                href={`https://admin.twicecommerce.com/booking/${orderId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-primary-600 hover:text-primary-800"
              >
                <span className="mr-1">Open in Admin</span>
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between py-2 border-b border-gray-200">
                <span className="text-gray-600">Status</span>
                {report.status === 'damage_found' ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                    <SafeIcon icon={FiIcons.FiAlertTriangle} className="mr-1" />
                    Issues Reported
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    <SafeIcon icon={FiIcons.FiCheckCircle} className="mr-1" />
                    No Issues
                  </span>
                )}
              </div>

              <div className="flex items-center justify-between py-2 border-b border-gray-200">
                <span className="text-gray-600">Order ID</span>
                <span className="font-medium">{report.order_id}</span>
              </div>

              <div className="flex items-center justify-between py-2 border-b border-gray-200">
                <span className="text-gray-600">Merchant ID</span>
                <span className="font-medium">{report.merchant_id}</span>
              </div>

              <div className="flex items-center justify-between py-2 border-b border-gray-200">
                <span className="text-gray-600">Submitted At</span>
                <span className="font-medium flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  {safeFormatDate(reportDate)}
                </span>
              </div>
            </div>

            {descriptionItems.length > 0 && (
              <div className="mt-6">
                <h3 className="text-md font-medium text-gray-900 mb-3">Reported Issues</h3>
                <div className="space-y-3">
                  {descriptionItems.map((item, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-3">
                      <div className="font-medium text-gray-800">{item.category}</div>
                      <div className="text-gray-600">{item.details || 'No details provided'}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Photo Gallery */}
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Image className="h-5 w-5 mr-2" />
              Photos
            </h2>

            {loading ? (
              <div className="flex justify-center items-center h-40">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
                <div className="flex">
                  <AlertTriangle className="h-5 w-5 mr-2" />
                  <span>{error}</span>
                </div>
              </div>
            ) : photos.length === 0 ? (
              <div className="text-center py-6 bg-gray-50 rounded-lg">
                <SafeIcon icon={FiIcons.FiImage} className="mx-auto h-10 w-10 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500">No photos were uploaded with this report</p>
              </div>
            ) : (
              <div>
                {/* Main selected photo */}
                <div className="mb-4 bg-gray-100 rounded-lg overflow-hidden">
                  {selectedPhoto && (
                    <img
                      src={selectedPhoto.url}
                      alt={`Photo ${selectedPhoto.file_name}`}
                      className="w-full h-auto max-h-96 object-contain"
                      onError={handleImageError}
                    />
                  )}
                </div>

                {/* Photo thumbnails */}
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                  {photos.map((photo) => (
                    <button
                      key={photo.id}
                      onClick={() => setSelectedPhoto(photo)}
                      className={`relative rounded-md overflow-hidden border-2 ${
                        selectedPhoto && selectedPhoto.id === photo.id
                          ? 'border-primary-500'
                          : 'border-transparent hover:border-gray-300'
                      }`}
                    >
                      <img
                        src={photo.url}
                        alt={`Thumbnail ${photo.file_name}`}
                        className="w-full h-16 object-cover"
                        onError={handleImageError}
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
            <div className="space-y-3">
              <a
                href={`https://admin.twicecommerce.com/booking/${orderId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary w-full flex items-center justify-center"
              >
                <SafeIcon icon={FiIcons.FiExternalLink} className="mr-2" />
                Open in Admin
              </a>

              {photos.length > 0 && selectedPhoto?.url && (
                <a
                  href={selectedPhoto.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary w-full flex items-center justify-center"
                >
                  <SafeIcon icon={FiIcons.FiMaximize2} className="mr-2" />
                  View Full-size Photo
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ReportDetail